import { Box, FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup, Typography } from '@mui/material';
import { ReactNode, useId } from 'react';
import { FieldErrors, get, useController, useFormContext } from 'react-hook-form';
import { z } from 'zod';

type RadioValue = string | boolean | number;

export interface IRadioTilesProps {
	name: string;
	groupLabel: string;
	required?: boolean;
	options: {
		label: string;
		subLabel?: string;
		value: RadioValue;
		headerContent?: ReactNode;
	}[];
	lovetFontAdjustment?: boolean;
}

const generateRadioTileStyles = (selected: boolean, hasHeader: boolean) => ({
	padding: '1rem',
	margin: 0,
	bgcolor: 'backgrounds.default',
	border: selected ? '0.125rem' : '0.0625rem',
	borderStyle: 'solid',
	borderRadius: hasHeader ? '0 0 0.5rem 0.5rem' : '0.5rem',
	borderColor: selected ? 'secondary.main' : 'neutrals.primary',
	'&:hover': {
		borderColor: 'secondary.main',
	},
	width: '100%',
});

// as name implies, this solves for 1 specific and annoying case.
const generateFontAdjustment = (lovetFontAdjustment: boolean) => {
	return lovetFontAdjustment ? { position: 'relative', top: '3px' } : {};
};

type ValueOf<T> = T[keyof T];

export const radioTileSchema = ((options) => {
	return {
		options,
		create: (fieldName: string, validation: ValueOf<typeof options>) => {
			return z.object({ [fieldName]: validation });
		},
	};
})({
	default: z.string().min(1, 'Required'),
	notRequired: z.any(),
});

export default function RadioTiles({
	name,
	groupLabel,
	options,
	required = false,
	lovetFontAdjustment = false,
}: Readonly<IRadioTilesProps>) {
	const { control } = useFormContext();
	const {
		field,
		formState: { errors },
	} = useController({ name, control });
	const labelId = useId();

	const error = get(errors, name) as FieldErrors;

	return (
		<FormControl fullWidth required={required} error={!!error}>
			<Typography my={3} variant="header3" id={labelId}>
				{groupLabel}
			</Typography>
			<RadioGroup
				name={name}
				onChange={field.onChange}
				value={field.value as RadioValue}
				aria-labelledby={labelId}
				sx={{
					'&.Mui-focused': {
						color: 'none', // or specify a fixed color
					},
				}}
			>
				{options.map((option) => (
					<Box key={`radio_tile_${option.value}`} mb="0.5rem">
						{option.headerContent}
						<FormControlLabel
							{...field}
							sx={generateRadioTileStyles(option.value === field.value, !!option.headerContent)}
							label={''}
							value={option.value}
							control={
								option?.subLabel ? (
									<Box display="flex" alignItems="center">
										<Radio value={option.value} />
										<Box display="flex" flexDirection="column" pl={2}>
											<Typography
												variant="bodyLargeSemiBold"
												color="text.interactive"
												data-test-id={`radio_tile_label_${option.value}`}
												py={0.5}
												sx={generateFontAdjustment(lovetFontAdjustment)}
											>
												{option.label}
											</Typography>
											<Typography
												variant="bodyMediumBook"
												data-test-id={`radio_tile_sub_label_${option.value}`}
												py={0.5}
												sx={generateFontAdjustment(lovetFontAdjustment)}
											>
												{option.subLabel}
											</Typography>
										</Box>
									</Box>
								) : (
									<Box display="flex" alignItems="center">
										<Radio value={option.value} />
										<Box>
											<Typography
												variant="bodyLargeSemiBold"
												color="text.interactive"
												data-test-id={`radio_tile_label_${option.value}`}
												sx={generateFontAdjustment(lovetFontAdjustment)}
											>
												{option.label}
											</Typography>
										</Box>
									</Box>
								)
							}
						/>
					</Box>
				))}
			</RadioGroup>
			<FormHelperText>
				<>{error?.message}</>
			</FormHelperText>
		</FormControl>
	);
}
